import {
  PlayArrow as PlayArrowIcon,
  Settings as SettingsIcon,
  Stop as StopIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  LinearProgress,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { generateTimetable, MockTimetableGenerator } from '../../lib/utils/mockGenerator';
import { useGeneratorStore } from '../../stores/generatorStore';
import { useTimetableStore } from '../../stores/timetableStore';
import { defaultGenerationSettings } from './MockData';
import { routes } from '../../routes/routes';

const GenerateTimetable: React.FC = () => {
  const navigate = useNavigate();
  const {
    settings,
    updateSettings,
    progress,
    setProgress,
    isGenerating,
    startGeneration,
    stopGeneration,
    completeGeneration,
    failGeneration,
    addHistoryRecord,
  } = useGeneratorStore();

  const { addTimetable, setCurrentTimetable } = useTimetableStore();

  const [generator, setGenerator] = useState<MockTimetableGenerator | null>(null);

  // Stop generator on unmount
  useEffect(() => {
    return () => {
      if (generator) {
        generator.stop();
      }
    };
  }, [generator]);

  const handleGenerate = async () => {
    try {
      startGeneration();

      const newGenerator = new MockTimetableGenerator(settings);
      setGenerator(newGenerator);

      newGenerator.setProgressCallback((progressValue, step, message) => {
        setProgress({
          status: 'generating',
          progress: progressValue,
          currentStep: step,
          message,
        });
      });

      const timetable = await newGenerator.generate();

      // Add timetable to store
      addTimetable(timetable);
      setCurrentTimetable(timetable);

      // Add to history
      const endTime = new Date();
      const startTime = progress.startTime || endTime;
      const duration = (endTime.getTime() - startTime.getTime()) / 1000;

      addHistoryRecord({
        id: `gen-${Date.now()}`,
        timetableId: timetable.id,
        timetableName: timetable.name,
        profileUsed: settings.profileId,
        profileName: getProfileName(settings.profileId),
        startTime,
        endTime,
        duration,
        status: timetable.stats.ruleViolations.hard > 0 ? 'failed' : 'success',
        lessonsGenerated: timetable.stats.totalLessons,
        conflictsFound: timetable.stats.conflictCount,
        hardRuleViolations: timetable.stats.ruleViolations.hard,
        softRuleViolations: timetable.stats.ruleViolations.soft,
        curriculumCoverage: timetable.stats.curriculumCoverage,
        teacherSatisfaction: timetable.stats.teacherPreferenceSatisfaction,
        roomUtilization: 90, // Mock value
        createdBy: 'admin-1',
      });

      completeGeneration(timetable.id);
    } catch (error) {
      failGeneration(error instanceof Error ? error.message : 'Generation failed');
    }
  };

  const handleStop = () => {
    if (generator) {
      generator.stop();
    }
    stopGeneration();
  };

  const handleViewTimetable = () => {
    if (progress.timetableId) {
      navigate(`${routes.timetable.view}/${progress.timetableId}`);
    }
  };

  const getProfileName = (profileId: string): string => {
    const profiles: Record<string, string> = {
      'profile-1': 'Strict Compliance',
      'profile-2': 'Balanced Optimization',
      'profile-3': 'Quick Generation',
      'profile-4': 'Teacher-Focused',
    };
    return profiles[profileId] || 'Unknown Profile';
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          Generate Timetable
        </Typography>
        <Button
          variant="outlined"
          startIcon={<SettingsIcon />}
          onClick={() => navigate(routes.generator.settings)}
          disabled={isGenerating}
        >
          Settings
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Settings Panel */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Generation Settings
            </Typography>

            <TextField
              fullWidth
              select
              label="Automation Profile"
              value={settings.profileId}
              onChange={(e) => updateSettings({ profileId: e.target.value })}
              disabled={isGenerating}
              margin="normal"
            >
              <MenuItem value="profile-1">Strict Compliance</MenuItem>
              <MenuItem value="profile-2">Balanced Optimization</MenuItem>
              <MenuItem value="profile-3">Quick Generation</MenuItem>
              <MenuItem value="profile-4">Teacher-Focused</MenuItem>
            </TextField>

            <TextField
              fullWidth
              type="number"
              label="Max Generation Time (seconds)"
              value={settings.maxGenerationTime}
              onChange={(e) => updateSettings({ maxGenerationTime: Number.parseInt(e.target.value) })}
              disabled={isGenerating}
              margin="normal"
            />

            <TextField
              fullWidth
              type="number"
              label="Max Soft Rule Violations"
              value={settings.maxSoftRuleViolations}
              onChange={(e) => updateSettings({ maxSoftRuleViolations: Number.parseInt(e.target.value) })}
              disabled={isGenerating || !settings.allowSoftRuleViolations}
              margin="normal"
            />

            <Box sx={{ mt: 3 }}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={<PlayArrowIcon />}
                onClick={handleGenerate}
                disabled={isGenerating}
              >
                Generate Timetable
              </Button>
            </Box>

            {isGenerating && (
              <Box sx={{ mt: 2 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  color="error"
                  startIcon={<StopIcon />}
                  onClick={handleStop}
                >
                  Stop Generation
                </Button>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Progress Panel */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Generation Progress
            </Typography>

            {progress.status === 'idle' && (
              <Alert severity="info">Ready to generate. Click "Generate Timetable" to begin.</Alert>
            )}

            {(progress.status === 'preparing' ||
              progress.status === 'generating' ||
              progress.status === 'validating') && (
                <>
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {progress.currentStep}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {progress.progress}%
                      </Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={progress.progress} />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {progress.message}
                  </Typography>
                </>
              )}

            {progress.status === 'complete' && (
              <>
                <Alert severity="success" sx={{ mb: 2 }}>
                  {progress.message}
                </Alert>
                <Button variant="contained" onClick={handleViewTimetable}>
                  View Generated Timetable
                </Button>
              </>
            )}

            {progress.status === 'error' && (
              <Alert severity="error">{progress.message}</Alert>
            )}

            {/* Quick Stats */}
            {progress.status !== 'idle' && (
              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Quick Stats
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Card>
                      <CardContent>
                        <Typography color="text.secondary" variant="body2">
                          Profile
                        </Typography>
                        <Typography variant="h6">{getProfileName(settings.profileId)}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6}>
                    <Card>
                      <CardContent>
                        <Typography color="text.secondary" variant="body2">
                          Max Time
                        </Typography>
                        <Typography variant="h6">{settings.maxGenerationTime}s</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  {progress.startTime && (
                    <Grid item xs={6}>
                      <Card>
                        <CardContent>
                          <Typography color="text.secondary" variant="body2">
                            Start Time
                          </Typography>
                          <Typography variant="body1">
                            {progress.startTime.toLocaleTimeString()}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}
                  {progress.endTime && (
                    <Grid item xs={6}>
                      <Card>
                        <CardContent>
                          <Typography color="text.secondary" variant="body2">
                            Duration
                          </Typography>
                          <Typography variant="body1">
                            {((progress.endTime.getTime() - (progress.startTime?.getTime() || 0)) / 1000).toFixed(1)}s
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default GenerateTimetable;
